import React, { useEffect, useMemo } from "react";
import { Switch, Route, Redirect, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { compose } from "redux";
import { Box, PublicLayout as PublicLayoutUI, ThemeProvider2 } from "@ui-kit/components/index.es";
import * as i18n from "util/i18n";
import * as configUtil from "util/config";

import urlBackground from "styles/images/login-background.png";
import ToastNotification from "v3/components/ToastNotification";
import LoginStep1V3 from "v3/pages/auth/LoginStep1";
import LoginStep2V3 from "v3/pages/auth/LoginStep2";
import LoginStep3V3 from "v3/pages/auth/LoginStep3";
import LoginStep4V3 from "v3/pages/auth/LoginStep4V3";
import SupportV3Content from "v3/pages/SupportV3";
import TermsAndConditionsV3Content from "v3/pages/TermsAndConditionsV3";
import PrivacyPolicyV3Content from "v3/pages/PrivacyPolicyV3";
import RegionSelectionV3 from "v3/pages/RegionSelectionV3";
import RecoveryUserpassStep0V3 from "v3/pages/recovery/RecoveryUserpassStep0V3";
import RecoveryUserpassStep1V3 from "v3/pages/recovery/RecoveryUserpassStep1V3";
import RecoveryUserpassStep2V3 from "v3/pages/recovery/RecoveryUserpassStep2V3";
import RecoveryUserpassStep3V3 from "v3/pages/recovery/RecoveryUserpassStep3V3";
import RecoveryPasswordStep1V3 from "v3/pages/recovery/RecoveryPasswordStep1V3";
import RecoveryPasswordStep2V3 from "v3/pages/recovery/RecoveryPasswordStep2V3";
import RecoveryPasswordStep3V3 from "v3/pages/recovery/RecoveryPasswordStep3V3";
import RecoveryPasswordStep4V3 from "v3/pages/recovery/RecoveryPasswordStep4V3";
import RecoveryPassAndUserpassStep2V3 from "v3/pages/recovery/RecoveryPassAndUserpassStep2V3";
import RecoveryPassAndUserpassStep3V3 from "v3/pages/recovery/RecoveryPassAndUserpassStep3V3";
import RecoveryInformationV3 from "v3/pages/recovery/RecoveryInformationV3";
import RecoveryInformationClaveTokenV3 from "v3/pages/recovery/RecoveryInformationClaveTokenV3";
import RecoveryInformationBiometricsV3 from "v3/pages/recovery/RecoveryInformationBiometricsV3";
import { resizableRoute } from "pages/_components/Resizable";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { selectors as loginSelectors } from "reducers/login";
import { selectors as recoveryPasswordSelectors } from "reducers/recoveryPassword";
import { selectors as configSelectors } from "reducers/config";
import { actions as campaignsActions, selectors as campaignsSelectors } from "reducers/campaigns";

import { getUnauthenticatedLoginPath, getLoginStep3Path } from "util/v3UxConfig";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getCustomSafeAreaInsets } from "util/safeAreaInsets";

const getStepKey = (path) => {
    if (path === "/v3/login" || path === "/v3/login/") {
        return "step1";
    }
    if (path === "/v3/login/step2") {
        return "step2";
    }
    if (path === "/v3/login/step3") {
        return "step3";
    }
    if (path === "/v3/login/step4") {
        return "step4";
    }
    return null;
};

const isStep1 = (path) => path === "/v3/login" || path === "/v3/login/";

const getFooterLinks = (history, region) => {
    if (history) {
        return [
            {
                label: i18n.get("global.privacy"),
                ariaLabel: i18n.get("global.privacy"),
                onClick: () => history.push("/v3/privacyPolicy"),
            },
            {
                label: i18n.get("global.termAndConditions"),
                ariaLabel: i18n.get("global.termAndConditions"),
                onClick: () => history.push("/v3/termsAndConditions"),
            },
            {
                label: i18n.get("global.support"),
                ariaLabel: i18n.get("global.support"),
                onClick: () => history.push("/v3/support"),
            },
            {
                label: region,
                ariaLabel: i18n.get("settings.changeRegion.link.a11y"),
                onClick: () => history.push("/v3/regionSelection"),
            },
        ];
    }
    return undefined;
};

const isRegionSelection = (path) => path === "/v3/regionSelection";

const getRecoveryUserpassBackPath = (path) => {
    if (path === "/v3/recoveryUserpass/step1") {
        return "/v3/recoveryUserpass/step0";
    }
    if (path === "/v3/recoveryUserpass/step2") {
        return "/v3/recoveryUserpass/step1";
    }
    if (path === "/v3/recoveryUserpass/step3") {
        return "/v3/recoveryUserpass/step2";
    }
    if (path === "/v3/recoveryUserpass/step0") {
        return "/v3/login";
    }
    return null;
};

const isRecoveryUserpassPath = (path) => path?.startsWith("/v3/recoveryUserpass/");

const getRecoveryPasswordBackPath = (path) => {
    if (path === "/v3/recoveryPassword/step1") {
        return "/v3/login";
    }
    if (path === "/v3/recoveryPassword/step2") {
        return "/v3/recoveryPassword/step1";
    }
    if (path === "/v3/recoveryPassword/step3") {
        return "/v3/recoveryPassword/step2";
    }
    if (path === "/v3/recoveryPassword/step4") {
        return "/v3/recoveryPassword/step3";
    }
    return null;
};

const getRecoveryPassAndUserpassBackPath = (path) => {
    if (path === "/v3/recoveryPassAndUserpass/step2") {
        return "/v3/recoveryUserpass/step1";
    }
    if (path === "/v3/recoveryPassAndUserpass/step3") {
        return "/v3/recoveryPassAndUserpass/step2";
    }
    return null;
};

const getRecoveryInformationBackPath = (path) => {
    if (path === "/v3/recoveryPassword/information") {
        return "/v3/recoveryPassword/step1";
    }
    if (path === "/v3/recoveryPassword/informationClaveToken") {
        return "/v3/recoveryPassword/step1";
    }
    if (path === "/v3/recoveryPassword/informationBiometrics") {
        return "/v3/recoveryPassword/step1";
    }
    return null;
};

const isRecoveryPath = (path) =>
    isRecoveryUserpassPath(path) ||
    path?.startsWith("/v3/recoveryPassword/") ||
    path?.startsWith("/v3/recoveryPassAndUserpass/");

const getLoginBackPath = (path, uxLoginImprovements) => {
    if (path === "/v3/login/step4" || path === "/v3/login/step4/") {
        return getLoginStep3Path(uxLoginImprovements);
    }
    return null;
};

function PublicLayout({
    dispatch,
    isDesktop,
    history,
    location,
    match,
    region,
    campaigns,
    rememberedUser,
    loginFetching,
    loginFetchingBiometric,
    loginFingerprintSubmitting,
    recoveryAsyncPending,
    uxLoginImprovements,
}) {
    const currentPath = location?.pathname || "";
    const loginFlowBusy = loginFetching || loginFetchingBiometric || loginFingerprintSubmitting;
    const inactiveFooterLinks = useMemo(() => {
        if (currentPath.startsWith("/v3/login")) {
            return loginFlowBusy;
        }
        if (
            currentPath.startsWith("/v3/recoveryUserpass/") ||
            currentPath.startsWith("/v3/recoveryPassword/") ||
            currentPath.startsWith("/v3/recoveryPassAndUserpass/")
        ) {
            return recoveryAsyncPending;
        }
        return false;
    }, [currentPath, loginFlowBusy, recoveryAsyncPending]);
    const stepKey = getStepKey(currentPath);
    const campaignSection = "login-section";
    const recoveryBackPath =
        getRecoveryUserpassBackPath(currentPath) ||
        getRecoveryPasswordBackPath(currentPath) ||
        getRecoveryPassAndUserpassBackPath(currentPath) ||
        getRecoveryInformationBackPath(currentPath);
    const hasRememberedCredentials = Boolean(
        rememberedUser?.document && rememberedUser?.documentType && rememberedUser?.username,
    );
    const hideBackOnRememberedStep2 = currentPath === "/v3/login/step2" && hasRememberedCredentials;
    const showBack =
        !hideBackOnRememberedStep2 &&
        (!isStep1(currentPath) || isRegionSelection(currentPath) || isRecoveryPath(currentPath)) &&
        history;
    const loginEntry = getUnauthenticatedLoginPath(uxLoginImprovements);
    const loginBackPath = getLoginBackPath(currentPath, uxLoginImprovements);
    const handleBack = showBack
        ? () => {
              if (isRegionSelection(currentPath)) {
                  history.push(loginEntry);
              } else if (recoveryBackPath) {
                  history.push(recoveryBackPath);
              } else if (loginBackPath) {
                  history.push(loginBackPath);
              } else {
                  history.push(loginEntry);
              }
          }
        : undefined;
    const extras = { isMobile: !isDesktop, isDesktop };
    const regionlabel = i18n.get(`settings.regions.label.${region}`);
    const baseUrl = configUtil.get("client.baseURL");

    useEffect(() => {
        if (!campaignSection) {
            return undefined;
        }

        dispatch(campaignsActions.loadCampaignList(campaignSection));
        return () => {
            dispatch(campaignsActions.removeCampaigns());
        };
    }, [dispatch, campaignSection]);

    const carouselImages = useMemo(
        () =>
            (campaigns || [])
                .map((campaign) => {
                    const imageUrl =
                        campaign?.imageUrl || (campaign?.image ? `data:image/png;base64,${campaign.image}` : "");
                    if (!imageUrl) {
                        return null;
                    }

                    const campaignUrl = campaign?.url || "";
                    const redirect =
                        campaignUrl && baseUrl && campaignUrl.includes(baseUrl)
                            ? campaignUrl.replace(baseUrl, "")
                            : campaignUrl;

                    return { img: imageUrl, redirect };
                })
                .filter(Boolean),
        [campaigns, baseUrl],
    );

    const renderLoginStep = (key) => {
        const routeProps = { location, match };
        const props = { ...routeProps, ...extras };
        if (key === "step1") {
            return <LoginStep1V3 {...props} pathname="v3/login" />;
        }
        if (key === "step2") {
            return <LoginStep2V3 {...props} pathname="v3/login/step2" />;
        }
        if (key === "step3") {
            return <LoginStep3V3 {...props} pathname="v3/login/step3" />;
        }
        if (key === "step4") {
            return <LoginStep4V3 {...props} pathname="v3/login/step4" />;
        }
        return null;
    };

    const customSafeAreaInsets = getCustomSafeAreaInsets();

    return (
        <ThemeProvider2
            isV3Layout={isUxDashboardImprovementsEnabled()}
            customSafeAreaInsetTop={customSafeAreaInsets.top}
            customSafeAreaInsetBottom={customSafeAreaInsets.bottom}>
            <Box className="v3-public-layout-enter" css={{ width: "100%", minHeight: "100%" }}>
                <ToastNotification />
                <PublicLayoutUI
                    urlBackground={urlBackground}
                    showFooter
                    handleBack={handleBack}
                    footerLinks={getFooterLinks(history, regionlabel)}
                    region={regionlabel}
                    carouselImages={carouselImages}
                    inactiveFooterLinks={inactiveFooterLinks}>
                    <Box key={currentPath} className="v3-login-step-enter" css={{ width: "100%" }}>
                        {stepKey ? (
                            renderLoginStep(stepKey)
                        ) : (
                            <Switch location={location}>
                                <Route exact path="/v3" render={() => <Redirect to={loginEntry} />} />
                                <Route exact path="/v3/support" component={SupportV3Content} />
                                <Route exact path="/v3/termsAndConditions" component={TermsAndConditionsV3Content} />
                                <Route exact path="/v3/privacyPolicy" component={PrivacyPolicyV3Content} />
                                <Route exact path="/v3/regionSelection" component={RegionSelectionV3} />
                                <Route exact path="/v3/recoveryUserpass/step0" component={RecoveryUserpassStep0V3} />
                                <Route exact path="/v3/recoveryUserpass/step1" component={RecoveryUserpassStep1V3} />
                                <Route exact path="/v3/recoveryUserpass/step2" component={RecoveryUserpassStep2V3} />
                                <Route exact path="/v3/recoveryUserpass/step3" component={RecoveryUserpassStep3V3} />
                                <Route exact path="/v3/recoveryPassword/step1" component={RecoveryPasswordStep1V3} />
                                <Route exact path="/v3/recoveryPassword/step2" component={RecoveryPasswordStep2V3} />
                                <Route exact path="/v3/recoveryPassword/step3" component={RecoveryPasswordStep3V3} />
                                <Route exact path="/v3/recoveryPassword/step4" component={RecoveryPasswordStep4V3} />
                                <Route
                                    exact
                                    path="/v3/recoveryPassAndUserpass/step2"
                                    component={RecoveryPassAndUserpassStep2V3}
                                />
                                <Route
                                    exact
                                    path="/v3/recoveryPassAndUserpass/step3"
                                    component={RecoveryPassAndUserpassStep3V3}
                                />
                                <Route
                                    exact
                                    path="/v3/recoveryPassword/information"
                                    component={RecoveryInformationV3}
                                />
                                <Route
                                    exact
                                    path="/v3/recoveryPassword/informationClaveToken"
                                    component={RecoveryInformationClaveTokenV3}
                                />
                                <Route
                                    exact
                                    path="/v3/recoveryPassword/informationBiometrics"
                                    component={RecoveryInformationBiometricsV3}
                                />
                            </Switch>
                        )}
                    </Box>
                </PublicLayoutUI>
            </Box>
        </ThemeProvider2>
    );
}

PublicLayout.propTypes = {
    dispatch: func.isRequired,
    history: shape({ push: func }).isRequired,
    location: shape({ pathname: string }).isRequired,
    match: shape({}).isRequired,
    isMobile: bool.isRequired,
    isDesktop: bool.isRequired,
    region: string.isRequired,
    campaigns: arrayOf(shape({})),
    rememberedUser: shape({}),
    loginFetching: bool,
    loginFetchingBiometric: bool,
    loginFingerprintSubmitting: bool,
    recoveryAsyncPending: bool,
    uxLoginImprovements: bool,
};

PublicLayout.defaultProps = {
    campaigns: [],
    rememberedUser: null,
    loginFetching: false,
    loginFetchingBiometric: false,
    loginFingerprintSubmitting: false,
    recoveryAsyncPending: false,
    uxLoginImprovements: false,
};

const mapStateToProps = (state) => ({
    region: loginSelectors.getRegion(state),
    campaigns: campaignsSelectors.getCampaigns(state),
    rememberedUser: loginSelectors.getRememberedUser(state),
    loginFetching: loginSelectors.getFetching(state),
    loginFetchingBiometric: loginSelectors.getFetchingBiometric(state),
    loginFingerprintSubmitting: loginSelectors.getFingerprintSubmiting(state),
    recoveryAsyncPending: recoveryPasswordSelectors.getAsyncPending(state),
    uxLoginImprovements: !!configSelectors.getConfigKey(state, "frontend.UXImprovements.Login.functionalities"),
});

export default compose(withRouter, connect(mapStateToProps), resizableRoute)(PublicLayout);
